import TranslationCache from "./TranslationCache";
import TranslationNode from "./TranslationNode";
import TranslationLinkNode from "./TranslationLinkNode";
import TranslationImageNode from "./TranslationImageNode";
import Communication from "../Communication";
import TokenizedText from "./TokenizedText";
import TranslationTextInputNode from "./TranslationTextInputNode";
import RulesHandling from "./RulesHandling";
import {
    debounce,
    generateUUID,
    IGNORED_NODE_NAME,
    EXTENDED_IGNORED_NODE_NAME,
    NODE_TOKENIZER_CUSTOM_ATTRS,
} from "./utils";

/**
 * Dynamic content handler is a complicated process,
 * the following is the first prototype or version of the handler.
 * 
 * The handler support the following:
 * - Translating content added (modal/popup)
 * - Translating changes in content
 * - Translating changes in link (link translation)
 * - Minimal/untested support for inline content
 * - Minimal check if we should translate text (see: src/DynamicContent/TranslationNode.js:shouldWeTranslate)
 */

let debugMode = false;

/**
 * Mapping of node element name into what tokenizer node to use
 */
const NODE_TOKENIZER_MAPPING = {
    "IMG": TranslationImageNode,
    "A": TranslationLinkNode,
    "TEXTAREA": TranslationTextInputNode,
    "INPUT": TranslationTextInputNode,
}

/**
 * Get tokenizer for the element
 * 
 * @param {Element} element The element to be tokenized
 * @return {typeof TranslationNode} The tokenizer
 */
function getTokenizer(element) {
    const nodeName = element.nodeName.toUpperCase();
    return NODE_TOKENIZER_MAPPING[nodeName] || TranslationNode;
}

/**
 * The CSS styling for loading/blur state
 * 
 * Will be injected on init
 * 
 * @type {string}
 */
const DYNAMIC_CONTENT_LOAD_STYLE = `
[data-linguise-loading] {
    text-shadow: 0 0 6px #8f8f8f;
    transition: text-shadow 0.3s ease-in-out;
    color: transparent !important;
    animation: linguise-loading-pulse 1.5s ease-in-out infinite;
}

@keyframes linguise-loading-pulse {
    0% {
        text-shadow: 0 0 6px #8f8f8f;
    }
    50% {
        text-shadow: 0 0 10px #8f8f8f;
    }
    100% {
        text-shadow: 0 0 6px #8f8f8f;
    }
}
`

const filteredAttributes = new Set();
for (const key in NODE_TOKENIZER_CUSTOM_ATTRS) {
    NODE_TOKENIZER_CUSTOM_ATTRS[key].forEach((attr) => {
        filteredAttributes.add(attr);
    });
}

/**
 * The common mutation observer config
 * 
 * @type {MutationObserverInit}
 */
const MutationObserverConfig = {
    childList: true,
    subtree: true,
    attributes: true,
    characterData: true,
    attributeFilter: [...filteredAttributes, "linguise-ignore"],
};

/**
 * Debounce the array of elements before calling the callback
 * @param {(elements: TranslationNode[]) => void} callback The callback to be called when the array is ready
 * @param {*} delay timeout delay
 * @returns {(elements: TranslationNode[]) => void} The debounced function
 */
function debounceArray(callback, delay) {
    /** @type {number | null} */
    let timeoutId = null;

    /**
     * The array of collected elements overtime
     *
     * @type {TranslationNode[]}
     */
    let updateArray = [];

    const processUpdate = () => {
        timeoutId = null;
        debugMode && console.log(
            "Linguise: Debounce is not called after timeout, executing with",
            updateArray.length,
            "items"
        );
        callback(updateArray);
        updateArray = [];
    };

    return (elements) => {
        debugMode && console.log("Linguise: Called debouncer with", elements.length, "items");

        updateArray = [...updateArray, ...elements];
        // reset timeout only if elements is not empty
        if (elements.length > 0) {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(processUpdate, delay);
        };
    }
}

/**
 * Deduplicate TranslationNode array
 * 
 * @param {TranslationNode[]} array The array to be deduplicated
 * @returns {TranslationNode[]} The deduplicated array
 */
function deduplicateTranslationArray(array) {
    const idSeen = new Set();
    const elementSeen = new Set();

    return array.filter((item) => {
        const duplicate = idSeen.has(item.id);
        idSeen.add(item.id);
        return !duplicate;
    }).filter((item) => {
        const duplicate = elementSeen.has(item.element);
        elementSeen.add(item.element);
        return !duplicate;
    });
}

/**
 * Merge node with the same parent element for contextful translation.
 * This will also convert the node into tokenized (HTML) string
 *
 * @param {TranslationNode[]} nodes The array to be merged
 * @returns {string}
 */
function createContextualTokenizedData(nodes) {
    /** @type {Map<Element, TranslationNode[]>} */
    const mergedParent = new Map();

    /** @type {TokenizedText[]} */
    const skippedNode = [];
    for (const node of nodes) {
        if (node.element === node.parentElement) {
            // most likely non-text node
            skippedNode.push(node);
            continue;
        }

        if (!node.parentElement || !node.mergeable) {
            // missing parent element or unmergeable node
            skippedNode.push(node);
            continue;
        }

        if (mergedParent.has(node.parentElement)) {
            mergedParent.get(node.parentElement).push(node);
        } else {
            mergedParent.set(node.parentElement, [node]);
        }
    }

    /** @type {string[]} */
    const tokenizedHTML = [];
    for (const [parent, children] of mergedParent) {
        if (children.length === 1) {
            tokenizedHTML.push(children[0].asHtml());
            continue;
        }

        // sort children by the node position
        // prefill with undefined
        /** @type {TranslationNode[]} */
        const sortedChildren = new Array(parent.childNodes.length).fill(undefined);
        for (const child of children) {
            for (let i = 0; i < parent.childNodes.length; i++) {
                if (parent.childNodes[i] === child.element) {
                    sortedChildren[i] = child;
                }
            }
        }

        const parentToken = `<div data-linguise-parent="${generateUUID()}">`
        const childrenToken = sortedChildren.filter((child) => child !== undefined).map((child) => {
            return child.asHtml().replace("<div", "<span").replace("</div>", "</span>");
        }).join("");
        tokenizedHTML.push(parentToken + childrenToken + "</div>");
    }

    for (const node of skippedNode) {
        tokenizedHTML.push(node.asHtml());
    }

    return tokenizedHTML.join("");
}

/**
 * @typedef {Function} ConfigFetch
 * @param {object} config
 * @param {(config: object) => void} readyCallback
 */

/**
 * The main class for handling dynamic content
 * 
 * There is some additional config that you can do to the window.lingConfig object:
 * 
 * ```html
 * <script>
 *     window.lingConfig = {
 *         apiKey: "YOUR_PUBLIC_API_KEY", // the public API key available from Linguise dashboard
 *         debounceDelay: 2500, // how long to wait before sending the request to server
 *         cacheTTL: 3 * 24 * 60 * 60 * 1000, // how long to keep the translation in cache
 *     };
 * </script>
 * ```
 */
class DynamicContent {
    /**
     * Initialize the dynamic content
     * 
     * There is some additional config that you can do to the window.lingConfig object:
     * 
     * ```html
     * <script>
     *     window.lingConfig = {
     *         apiKey: "YOUR_PUBLIC_API_KEY", // the public API key available from Linguise dashboard
     *         debounceDelay: 2500, // how long to wait before sending the request to server
     *         cacheTTL: 3 * 24 * 60 * 60 * 1000, // how long to keep the translation in cache
     *     };
     * </script>
     * ```
     * 
     * @param {ConfigFetch} configFetch The function to fetch the config
     * @param {string | null} translate_endpoint The endpoint to be used for translation, can be overriden, default to translate.linguise.com
     */
    constructor(configFetch, translate_endpoint = null) {
        this.config = {
            apiKey: undefined,
            debounceDelay: 2500,
            cacheTTL: 3 * 24 * 60 * 60 * 1000,
            debug: false,
            // Force disable dynamic translations
            forceDisable: false,
            // If dryRun enabled, we don't send any request to server
            dryRun: false,
            // More testing related config
            domainId: null, // domain ID
            domainUrl: null, // domain URL
            languageOverride: null, // override the translation language to be used
            // API related config
            baseUrl: `${window.location.protocol}//${window.location.host}`,
            // Animation related settings
            blurAnimation: true,
            // Other settings
            isWpBrickDetected: false,
        };
        this.debouncedProcessTranslation = debounceArray(this.processTranslation.bind(this), this.config.debounceDelay);
        this.debounceDequeLockedNode = debounce(this.dequeLockedNode.bind(this), 500);

        this.config_ready = false;
        /** @type {TranslationNode[]} node to be translated while waiting for config to be ready. */
        this._queued_translations = [];
        /** @type {Node[]} node that is locked while waiting for the apply to be done. */
        this._locked_node = [];
        /** @type {string | null} Translation endpoint */
        this._translate_endpoint = translate_endpoint;

        let currentDocLang = document.documentElement.lang || "";

        // strip the country code (except zh-)
        if (currentDocLang.includes("-") && !currentDocLang.startsWith("zh-")) {
            currentDocLang = currentDocLang.split("-")[0];
        }

        currentDocLang = currentDocLang.toLowerCase();
        /**
         * The current language
         * 
         * @type {string | null}
         */
        this.current_language = null;
        /**
         * The current document language
         * 
         * @type {string}
         */
        this.current_document_language = currentDocLang;

        /**
         * The current observer
         * 
         * @type {MutationObserver | null}
         */
        this.observer = null;
        /**
         * The list of child/shadow DOM observer
         * 
         * @type {Map<Node, MutationObserver>}
         */
        this.shadow_observers = new Map();

        /**
         * The list of rules exclusions to be applied
         * 
         * @type {RulesHandling}
         */
        this.rules = new RulesHandling(); // bootstrap with empty rules

        /**
         * The list of temporary excluded nodes
         *
         * @type {Element[]}
         */
        this.temporary_excluded_nodes = [];

        /**
         * The function to fetch the config
         * 
         * @type {ConfigFetch}
         */
        this._config_fetch = configFetch;

        /**
         * Is linguise editor active?
         */
        this._linguise_editor = false;

        // Start process
        if (document.readyState === 'loading') {
            // Wait page to be loaded
            addEventListener('DOMContentLoaded', () => {
                this.initialize().then(() => {
                    // do not block the observer init
                    this.wrapFetchConfig();
                }).catch((error) => {
                    console.error("Linguise: Failed to initialize dynamic content", error);
                    this.cancelProcess();
                });;
            });
        } else {
            // Page already loaded
            this.initialize().then(() => {
                // do not block the observer init
                this.wrapFetchConfig();
            }).catch((error) => {
                console.error("Linguise: Failed to initialize dynamic content", error);
                this.cancelProcess();
            });
        }
    }

    /**
     * Call the config fetch function
     */
    wrapFetchConfig() {
        if (typeof this._config_fetch !== "function") {
            return;
        }

        this._config_fetch(this.config, async (currentConfig) => {
            const storedConfig = Communication.getStoredMetadata();

            // Merge the config with stored config so we get Rules information for WP and Joomla
            currentConfig = Object.assign({}, storedConfig || {}, currentConfig);

            if (currentConfig.debug) {
                this.config.debug = true;
            }

            if (!currentConfig.dynamic_translations.enabled) {
                this.config.debug && console.log("Linguise: Dynamic translation is disabled, cancelling");
                this.cancelProcess();
                return;
            }

            if (!currentConfig.public_key) {
                this.config.debug && console.log("Linguise: Public key is not set, cancelling");
                this.cancelProcess();
                return;
            }

            this.config.debug && console.log("Linguise: Config is ready, processing queued translations");
            let language = currentConfig.language;
            for (const index in currentConfig.languages) {
                if (currentConfig.languages[index].code === this.current_document_language) {
                    language = this.current_document_language;
                    break;
                }
            }

            if (this.config.languageOverride) {
                language = this.config.languageOverride;
            }

            if (currentConfig.language === language) {
                this.config.debug && console.log("Linguise: Document language is the same as current language, cancelling");
                this.cancelProcess();
                return;
            }

            // set config
            this.current_language = language;
            this.config.apiKey = currentConfig.public_key;
            this.config.baseUrl = currentConfig.domain || currentConfig.url;

            const rules = currentConfig.rules || [];
            this.rules = new RulesHandling(rules);

            if (this.rules.shouldPauseDynamicContent()) {
                this.config.debug && console.log("Linguise: Dynamic content disabled via URL exclusions rules");
                this.cancelProcess();
                return;
            }

            Communication.setPublicKey(currentConfig.public_key);
            // set as ready
            this.config_ready = true;
            this.isWpBrickDetected = window?.wpgb_settings !== undefined;

            if (Array.isArray(currentConfig.cached_selectors)) {
                /** @type {Element[]} */
                const cachedSelectors = currentConfig.cached_selectors
                    .map((selector) => document.querySelector(selector))
                    .filter((el) => el !== null);
                this.temporary_excluded_nodes = cachedSelectors;
            }

            // blur all queueed parent element
            this._queued_translations = this._queued_translations.filter((defer) => {
                // Do another preflight check
                return this.isElementPreflightAllow(defer.node, defer.parentElement);
            });
            this._queued_translations.forEach((defer) => {
                if (this.config.blurAnimation) {
                    defer.blurParent();
                }
            });

            // do initial rules processing
            /** @type {TranslationNode[]} */
            const initialNodes = this.uniqueElementNode(this.rules.collectDynamicNodes())
                .filter(([node, parentElement]) => {
                    return this.isElementPreflightAllow(node, parentElement);
                })
                .map(([node, parentElement]) => {
                    const tokenNode = this.applyCacheOrTranslate(node, parentElement);
                    if (tokenNode) {
                        return tokenNode;
                    }
                })
                .filter((node) => node !== undefined);

            initialNodes.forEach((defer) => {
                // blur text
                defer.blurParent();

                // detach editor
                defer.detachEditor();
            });

            // process the queue
            this.debouncedProcessTranslation([...initialNodes, ...this._queued_translations]);
        }).catch((error) => {
            console.error("Linguise: Failed to fetch config", error);
            this.cancelProcess();
        });
    }

    /**
     * Translate the content
     * 
     * @param {string} htmlContent The HTML content to be translated
     * @return {[string, boolean]} The translated HTML content and if the translation is successful
     */
    async _translate(htmlContent) {
        if (this.config.dryRun) {
            return [htmlContent, true];
        }

        const selected_language = this.config.languageOverride || this.current_language;

        try {
            const wrappedHtmlContent = `<div class="linguise-dynamic-wrapper">${htmlContent}</div>`;
            const translations = await Communication.translate(
                this.config.baseUrl,
                selected_language,
                wrappedHtmlContent,
                this._translate_endpoint,
                this.setLinguiseEditorActiveStatus(),
            );

            return [translations.content, true];
        } catch (error) {
            console.error("Linguise: Failed to translate content", error);
            // return original content
            return [htmlContent, false];
        }
    }

    /**
     * Process the node to be translated
     *
     * @param {TranslationNode[]} toBeTranslated the array of TranslationNode to be translated
     * @returns {void}
     */
    async processTranslation(toBeTranslated) {
        // Queue translation if config is not ready
        if (!this.config_ready) {
            this.config.debug && console.log("Linguise: Config is not ready, queueing translations");
            this._queued_translations.push(...toBeTranslated);
            return;
        }

        this.setLinguiseEditorActiveStatus();

        toBeTranslated.forEach((node) => {
            try {
                this.rules.attachTextRules(node);
            } catch (e) {
                this.config.debug && console.warn("Linguise: Failed to attach text rules", e);
            }
        });

        const tokenized = toBeTranslated
            .map((defer) => {
                try {
                    return defer.tokenize();
                } catch {
                    // tokenizer would throw error if the element is removed from DOM or something,
                    // this would ensure that we don't crash the whole process

                    // Also unblur the parent element
                    defer.unblurParent();
                    return [];
                }
            })
            .flat();

        // check cache first
        const uncachedResponse = tokenized
            .filter((token) => {
                return !token.isCached();
            })
            .map((token) => token.node);

        const uncachedDeduplicated = deduplicateTranslationArray(
            // Check if the element is attached to DOM
            uncachedResponse.filter((defer) => defer.element.isConnected)
        );

        toBeTranslated.forEach((defer) => {
            defer.blurParent();
        })

        this.config.debug && console.log("Linguise: Found uncached", uncachedDeduplicated);
        this.config.debug && console.debug("Linguise: Current translation queue:", toBeTranslated);
        this.config.debug && console.debug("Linguise: Current uncached response:", uncachedResponse);
        this.config.debug && console.debug("Linguise: Uncached disconnected:", uncachedResponse.filter((defer) => !defer.element.isConnected));

        // to be translated
        const asHTML = createContextualTokenizedData(uncachedDeduplicated);
        this.config.debug && console.debug("Linguise: Translating", asHTML);
        if (!asHTML.length) {
            // Unblur all parent element
            toBeTranslated.forEach((defer) => {
                defer.unblurParent();
            });

            this.config.debug && console.log("Linguise: Nothing to be translated to server");
            return;
        }

        // send translation
        this.config.debug && console.log("Linguise: Sending translation to server...");
        const [translated, isSuccess] = await this._translate(asHTML);

        toBeTranslated.forEach((defer) => {
            defer.unblurParent();
        });

        if (!translated) {
            this.config.debug && console.log("Linguise: Translation failed, cancelling");
            return;
        }

        // Process the translated text
        this.config.debug && console.log("  Linguise: Received translation from server...");
        const translatedElement = document.createElement("div");
        translatedElement.innerHTML = translated;
        this.config.debug && console.debug("   Linguise: Translated content", translatedElement);

        // update the DOM
        this.config.debug && console.log("  Linguise: Updating DOM...");
        translatedElement.querySelectorAll("[data-linguise-id]").forEach((element) => {
            // Convert element token
            const replToken = TokenizedText.fromElement(element);
            if (!replToken) {
                return;
            }

            const defer = toBeTranslated.find((defer) => defer.id === replToken.id);
            if (!defer) {
                return;
            }

            // check if element is attached to DOM (in case it's removed)
            if (!defer.element.isConnected) {
                this.config.debug && console.log("  Linguise: Element is not attached to DOM, ignoring");
                return;
            }

            // only applyChange if the current element is still the same
            if (defer.isElementModified(replToken.index)) {
                this.config.debug && console.log("  Linguise: Element is changed, ignoring", defer);
                return;
            }

            this.enqueLockedNode(defer.element);
            defer.applyChange(
                replToken,
                this.current_language,
                {
                    dryRun: this.config.dryRun,
                    skipCache: !isSuccess,
                }
            );
        });
    }

    /**
     * Observe shadow DOM
     * @param {Element} node The node to be observed
     */
    attachShadowObserver(node) {
        // Check if the node is already observed
        if (this.shadow_observers.has(node)) {
            return;
        }

        const observer = new MutationObserver((mutations, observer) => {
            this.observerCallback(mutations, observer);
            // Check if shadow root is disconnected
            if (!node.isConnected) {
                this.config.debug && console.log("Linguise: Disconnecting shadow observer from observer", node);
                observer.disconnect();
                this.shadow_observers.delete(node);
            }
        });

        this.config.debug && console.log("Linguise: Attaching shadow observer", node);
        observer.observe(node, MutationObserverConfig);
        this.shadow_observers.set(node, observer);
    }

    /**
     * Collect all possible nodes from the baseNode
     *
     * @param {Node} baseNode the base node to be collected on
     * @param {[Node, Element][]} currentNodes The current nodes collected
     * @returns {[Node, Element][]} The collected text nodes
     */
    collectChildNodes(baseNode, currentNodes = []) {
        if (baseNode.nodeType === Node.TEXT_NODE) {
            currentNodes.push([baseNode, baseNode.parentElement]);
        } else if (baseNode.nodeName === "IMG" && (baseNode.hasAttribute("src") || baseNode.hasAttribute("alt") || baseNode.hasAttribute("title"))) {
            // Image node
            currentNodes.push([baseNode, baseNode]);
        } else if (baseNode.nodeName === "A" && baseNode.hasAttribute("href")) {
            // Link node
            currentNodes.push([baseNode, baseNode]);
        } else if (["TEXTAREA", "INPUT"].includes(baseNode.nodeName) && baseNode.hasAttribute("placeholder")) {
            // Textarea/input node
            currentNodes.push([baseNode, baseNode]);
        }

        // Collect more nodes
        for (let i = 0; i < baseNode.childNodes.length; i++) {
            this.collectChildNodes(baseNode.childNodes[i], currentNodes);
        }

        // Check for shadow DOM
        if (baseNode.nodeType === Node.ELEMENT_NODE && baseNode.shadowRoot) {
            // Attach shadow observer
            this.attachShadowObserver(baseNode.shadowRoot);
            // Collect shadow DOM nodes
            this.collectChildNodes(baseNode.shadowRoot, currentNodes);
        }
        return currentNodes;
    }

    /**
     * Get unique element nodes
     *
     * @param {[Node, Element][]} textNodes The text nodes to be filtered
     * @return {[Node, Element][]} The unique text nodes
     */
    uniqueElementNode(textNodes) {
        const seen = new Set();
        const unique = [];
        for (const [textNode, parent] of textNodes) {
            if (seen.has(textNode)) {
                continue;
            }
            seen.add(textNode);
            unique.push([textNode, parent]);
        }
        return unique;
    }

    /**
     * Check and apply cache if exists, otherwise return the node to be translated
     * @param {Element} element The main element to be translated
     * @param {Element} parentElement parent element if the element is #text
     * @returns {TranslationNode | undefined}
     */
    applyCacheOrTranslate(element, parentElement) {
        const cacheCounter = [];
        const TokImpl = getTokenizer(element);
        const nodeToken = new TokImpl(element, parentElement);

        // Skip anything "locked"
        if (this._locked_node.includes(element)) {
            this.config.debug && console.debug('Linguise: Skipping locked node', nodeToken);
            return;
        }

        // Use tokenize since it handle multiple text node
        /** @type {import("./TokenizedText")[]} */
        let tokenized = [];
        try {
            tokenized = nodeToken.tokenize();
        } catch {
            // tokenizer would throw error if the element is removed from DOM or something,
            // this would ensure that we don't crash the whole process
            return;
        }

        // Check if linguise editor active
        if (this.setLinguiseEditorActiveStatus()) {
            // If yes, do not use cache
            this.config.debug && console.debug('Linguise: Linguise editor is active, skipping cache');
            return nodeToken;
        }

        // This mapping will return true if the token is cached and applied, false otherwise
        // We check if all token is cached, if not, we translate the whole thing
        const currentlyCached = tokenized.map((token) => {

            // (wordpress) skip cache for frontend if WP GB is detected,
            // (wordpress) if not skip the cache, WP grid builder will replace post url using original url
            if (this.isWpBrickDetected){
                return false;   
            }

            // Text cache
            const cached = TranslationCache.get(token.text, this.current_language || this.current_document_language, token.subId);
            if (cached) {
                const replToken = new TokenizedText(null, token.index, cached);
                if (token.subId) {
                    replToken.subId = token.subId;
                }
                // fallback to document language since we might not have initialized the current language
                this.enqueLockedNode(nodeToken.element);
                nodeToken.applyChange(replToken, this.current_language || this.current_document_language, {
                    dryRun: this.config.dryRun,
                });
                return true;
            }

            // Meta extra cache
            for (const [metaKey, metaVal] of Object.entries(token.metaExtra || {})) {
                const metaCached = TranslationCache.get(metaVal, this.current_language || this.current_document_language, token.subId);

                if (metaCached) {
                    const metaExtra = {};
                    metaExtra[metaKey] = metaCached;
                    const replToken = new TokenizedText(null, token.index, token.text, metaExtra);
                    if (token.subId) {
                        replToken.subId = token.subId;
                    }
                    this.enqueLockedNode(nodeToken.element);
                    // fallback to document language since we might not have initialized the current language
                    nodeToken.applyChange(replToken, this.current_language || this.current_document_language, {
                        dryRun: this.config.dryRun,
                    });
                    return true;
                }
            }
            return false;
        });

        if (currentlyCached.every((cached) => cached)) {
            // all cached, return undefined so we don't need to translate
            return;
        }
        // return the nodeToken so we can translate any uncached token
        return nodeToken;
    }

    cancelProcess() {
        if (this.observer !== null) {
            this.observer.disconnect();
        }

        // unblur all parent element
        this._queued_translations.forEach((defer) => {
            defer.unblurParent();
        });

        this.debounceDequeLockedNode();
    }

    extractScriptParam() {
        if (!document.currentScript) {
            return {};
        }

        const url = new URL(document.currentScript.src);

        const apiKey = url.searchParams.get("d");
        const domainId = url.searchParams.get("i");

        const config = {};
        if (apiKey) {
            config.apiKey = apiKey;
        }
        if (domainId) {
            config.domainId = domainId;
        }

        return config;
    }

    /**
     * Walk up the node and check if the node is linguise related (or reach <body> tag)
     * @param {Node} node base node to check
     * @returns {boolean} true if the node is linguise related
     */
    isLinguiseRelated(node) {
        if (!node) {
            return false;
        }
        if (node.nodeName === "BODY") {
            return false;
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.id && (node.id.startsWith("linguise_") || node.id.startsWith("linguise-"))) {
                return true;
            }
            if (typeof node.className === 'string' && (node.className.includes("linguise_") || node.className.includes('linguise-'))) {
                return true;
            }
        }
        return this.isLinguiseRelated(node.parentNode);
    }

    /**
     * Check and set if linguise editor is active
     */
    setLinguiseEditorActiveStatus() {
        if (this._linguise_editor) {
            return true;
        }

        const editorStyle = document.querySelector("link#linguise_editor_style");
        if (editorStyle) {
            this._linguise_editor = true;
            return true;
        }

        const editorToolbar = document.querySelector("div#linguise_editor_toolbar");

        if (editorToolbar) {
            this._linguise_editor = true;
            return true;
        }

        return false;
    }

    /**
     * Do cleanup for locked node
     */
    dequeLockedNode() {
        // Get current locked node and also remove from temporary excluded node
        this.temporary_excluded_nodes = this.temporary_excluded_nodes.filter((node) => !this._locked_node.includes(node));
        this.config.debug && console.log("Linguise: Dequeue-ing locked node", this._locked_node.length);
        this.config.debug && console.debug("Linguise: Locked node removal", this._locked_node);
        this._locked_node = [];
    }

    /**
     * Add node to locked node
     * @param {Node|Element} node
     */
    enqueLockedNode(node) {
        this._locked_node.push(node);
        this.debounceDequeLockedNode();
    }

    /**
     * Check if the node is shadow root
     * @param {ShadowRoot | Element | Node} node The node to be checked
     * @returns {boolean}
     */
    isShadowRoot(node) {
        if (!node) {
            // Node is null/undefined
            return false;
        }
        return node.nodeType === Node.DOCUMENT_FRAGMENT_NODE && node.host;
    }

    /**
     * Check if the element contains any the ignored attributes
     * @param {HTMLElement} element the element to check
     */
    isIgnoredAttribute(element) {
        const ignoreAttributes = [
            'linguise-ignore',
            'data-linguise-loading',
            'data-linguise-editor-active',
            'data-linguise-editor-wrap',
        ];

        if (typeof element.hasAttribute === 'function') {
            // Check our custom attribute
            return ignoreAttributes.map((attr) => element.hasAttribute(attr)).some((el) => el);
        }

        if (typeof element.getAttribute === 'function') {
            // Check translate="no"
            return element.getAttribute('translate')?.toLowerCase() === 'no';
        }

        return false;
    }

    /**
     * Check if the element is allowed to be translated
     * @param {Node} element Base node
     * @param {Element} parentElement Parent element of this node
     * @returns {boolean} true if the element is allowed to be translated
     */
    isElementPreflightAllow(element, parentElement) {
        // Check if element and parentElement is a shadow root, if yes we need to get the actual host that we want to check.
        /** @type {Node} */
        const elementRoot = this.isShadowRoot(element) ? element.host : element;

        // Is element exist in DOM
        if (!elementRoot.isConnected) {
            return false;
        }

        // Ignore comment node
        if (elementRoot.nodeType === Node.COMMENT_NODE) {
            return false;
        }

        // Ignore linguise related switcher (prefixed by linguise_ in `id` attribute)
        if (this.isLinguiseRelated(elementRoot)) {
            return false;
        }

        /** @type {HTMLElement} */
        const parentElementRoot = this.isShadowRoot(parentElement) ? parentElement.host : parentElement;

        // ignore script, style, etc
        if (elementRoot.nodeType !== Node.TEXT_NODE && IGNORED_NODE_NAME.includes(elementRoot.nodeName.toUpperCase())) {
            return false;
            // Also check the parent element if text node
        } else if (elementRoot.nodeType === Node.TEXT_NODE && parentElementRoot && EXTENDED_IGNORED_NODE_NAME.includes(parentElementRoot.nodeName.toUpperCase())) {
            return false;
        }

        // ignore anything marked with linguise-ignore attribute
        // used for explicit marking in case the user don't want to translate this dynamic content.
        if ((elementRoot.nodeType === Node.ELEMENT_NODE && this.isIgnoredAttribute(elementRoot)) || this.isIgnoredAttribute(parentElementRoot)) {
            return false;
        }

        // Check if excluded by rules
        if (this.rules.isElementRulesExcluded(elementRoot)) {
            return false;
        }

        // Check if temporary excluded by rules
        for (const exNode of this.temporary_excluded_nodes) {
            if (exNode === elementRoot || (parentElementRoot && exNode === parentElementRoot)) {
                this.enqueLockedNode(exNode);
                return false;
            }

            if (exNode.contains(elementRoot)) {
                this.enqueLockedNode(exNode);
                return false;
            }
        }

        return true;
    }

    /**
     * The main observer callback
     * 
     * Used to collect all the mutation and process it
     * @param {MutationRecord[]} mutationsList 
     * @param {MutationObserver} observer 
     */
    observerCallback(mutationsList, observer) {
        /** @type {TranslationNode[]} */
        const toBeTranslated = [];

        mutationsList.forEach((mutation) => {
            const element = mutation.target;
            // check if the element is #text
            // https://developer.mozilla.org/en-US/docs/Web/API/Node/nodeType
            let parentElement = mutation.target;
            if (element.nodeType === Node.TEXT_NODE) {
                // if yes, get parent element
                parentElement = element.parentElement;
            }

            if (mutation.type === "attributes" && element.nodeType === Node.ELEMENT_NODE) {
                // Check if this attribute changes is allowed
                if (!this.isElementPreflightAllow(element, parentElement)) {
                    return;
                }

                const attributeLists = NODE_TOKENIZER_CUSTOM_ATTRS[element.nodeName.toUpperCase()] || [];
                if (attributeLists.includes(mutation.attributeName)) {
                    // process the element
                    const tokenNode = this.applyCacheOrTranslate(element, parentElement);
                    if (tokenNode) {
                        toBeTranslated.push(tokenNode);
                    }
                }
            } else if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
                // deepsearch all addedNodes
                /** @type {TranslationNode[]} */
                const extendedTextNodes = [];
                mutation.addedNodes.forEach((addedNode) => {
                    const textNodes = this.uniqueElementNode(this.collectChildNodes(addedNode));
                    extendedTextNodes.push(...textNodes);
                });

                // deduplicate text node
                const extendedDeduplicated = this.uniqueElementNode(extendedTextNodes)
                    // do the same preflight check
                    .filter(([textNode, parent]) => {
                        return this.isElementPreflightAllow(textNode, parent);
                    });
                if (extendedDeduplicated.length > 0) {
                    // make into deferer
                    const deferrers = extendedDeduplicated
                        .map(([textNode, parent]) => {
                            const defer = this.applyCacheOrTranslate(textNode, parent);
                            return defer;
                        })
                        // filter out null
                        .filter((defer) => !!defer)
                    // blur each parent element (since some element are text)
                    deferrers.forEach((defer) => {
                        if (this.config_ready && this.config.blurAnimation) {
                            defer.blurParent();
                        }
                    });
                    toBeTranslated.push(...deferrers);
                }
            } else if (mutation.type === "characterData" && element.nodeType === Node.TEXT_NODE) {
                // Check if this text node changes is allowed
                if (!this.isElementPreflightAllow(element, parentElement)) {
                    return;
                }

                // add the following mutation
                const tokenNode = this.applyCacheOrTranslate(element, parentElement);
                if (tokenNode) {
                    if (this.config_ready) {
                        tokenNode.blurParent();
                    }
                    toBeTranslated.push(tokenNode);
                }
            }
        });

        if (!toBeTranslated.length) {
            return;
        }

        // Remove editor metadata when ready
        if (this.config_ready) {
            toBeTranslated.forEach((defer) => defer.detachEditor());
        }

        this.debouncedProcessTranslation(toBeTranslated);
    }

    /**
     * Collect all available shadow nodes
     * @returns {Promise<Element[]>} The shadow nodes
     */
    async collectShadowNodes() {
        const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, (node) => {
            if (!node.isConnected) {
                return NodeFilter.FILTER_REJECT;
            }

            // Check if the node is shadow DOM
            if (node.shadowRoot) {
                return NodeFilter.FILTER_ACCEPT;
            }

            // Skip everything else
            return NodeFilter.FILTER_SKIP;
        });

        /** @type {Element[]} */
        const shadowNodes = [];
        while (walker.nextNode()) {
            shadowNodes.push(walker.currentNode);
        }

        return shadowNodes;
    }

    /**
     * Entrypoint for the dynamic content processor.
     */
    async initialize() {
        if (window === undefined) {
            throw new Error("Linguise: please run this on browser");
        }

        // attach the loading style
        if (document.getElementById("linguise-dynamic-content-style")) {
            // remove previous style
            document.getElementById("linguise-dynamic-content-style").remove();
        }
        const styleElement = document.createElement("style");
        styleElement.id = "linguise-dynamic-content-style";
        styleElement.innerHTML = DYNAMIC_CONTENT_LOAD_STYLE;
        document.head.appendChild(styleElement);

        const scriptParam = this.extractScriptParam();

        const mergedConfig = Object.assign({}, this.config, scriptParam, window.lingConfig || {});

        if (!window.document.documentElement.lang) {
            throw new Error("Linguise: document.lang is not set, disabling dynamic content");
        }

        if (!this._config_fetch) {
            throw new Error("Linguise: configFetch is not set, disabling dynamic content");
        }

        this.config = mergedConfig;
        if (this.config.forceDisable) {
            this.config.debug && console.log("Linguise: forceDisable is set, disabling dynamic content");
            return;
        }

        debugMode = this.config.debug;

        TranslationCache.importFromStorage();
        TranslationCache.setMaxTTL(this.config.cacheTTL);

        if (this.observer !== null) {
            // disconnect from previous observer
            this.observer.disconnect();
        }

        // create new observer
        this.observer = new MutationObserver((mutations, observer) => {
            this.observerCallback(mutations, observer);
        });

        this.config.debug && console.log("Linguise: Attaching all shadow DOM observers");
        const shadowNodes = await this.collectShadowNodes();
        shadowNodes.forEach((node) => {
            if (node.shadowRoot) {
                this.attachShadowObserver(node.shadowRoot);
            }
        });

        this.config.debug && console.log("Linguise: Starting observer", mergedConfig);
        // XXX: This will not include modal outside the document.body
        //      One recommendation is to ask the user to put it inside body tag because no one really
        //      should put anything outside body tag for element that will be displayed.
        this.observer.observe(document.body, MutationObserverConfig);
    }
}

export default DynamicContent;
